import { useState } from "react"
import { Brain, Check, ChevronsUpDown, Lock } from "lucide-react"
import { cn } from "@/lib/utils"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"

/**
 * OpenRouter's own reasoning-effort values.
 *
 * Was `"low" | "medium" | "high"` from when effort secretly selected a model.
 * It has been out of step with what the composer actually passes since the model
 * picker shipped, and nothing caught it because BotDetail is .jsx — so widening
 * it is a fix, not a generalisation. Which of these a given model offers is a
 * per-model fact from the server, not a decision made here.
 */
export type Effort = "none" | "minimal" | "low" | "medium" | "high" | "xhigh" | "max"

/**
 * Trigger label when the model offers no efforts at all.
 *
 * Not "Disabled": that is the label for effort `none`, a tier the user can
 * choose. This is the absence of the dial, and saying "Disabled" would report a
 * choice nobody made.
 */
const NO_REASONING_LABEL = "No reasoning"

export interface EffortOption {
  value: Effort
  label: string
  description: string
  /** Not included in the current plan: shown with a lock, routes to the upgrade flow. */
  locked?: boolean
}

export interface EffortSelectorProps {
  value: Effort
  options: EffortOption[]
  onChange: (value: Effort) => void
  /** Picking a locked tier. Fires instead of onChange, so the selection is untouched. */
  onLockedSelect?: (value: Effort) => void
  /** The chosen model cannot reason at all: rows render inert, with an explanation. */
  disabled?: boolean
  className?: string
}

/**
 * Picks how hard the model reasons about the next message. More effort means a
 * better answer, a slower reply, and a faster burn of the plan's AI allowance —
 * so the trade-off is spelled out on every option.
 *
 * **This is the only reasoning control.** "Disabled" is the cheapest option in
 * the list, not a separate switch. There used to be a "Thinking" tick box beside
 * it, and the two disagreed: `effort` is a field inside OpenRouter's `reasoning`
 * object and `none` is one of its values, so unticking the box silently threw the
 * chosen effort away.
 *
 * Tiers a plan doesn't include are still listed rather than hidden: seeing what
 * Medium and High offer is the point of the lock.
 *
 * A model with NO reasoning at all arrives here as an EMPTY options array — 128
 * of OpenRouter's 414 models are in that position, and `tbl_model` says so with
 * an empty `reasoning_efforts`. That is a normal state, not an error, so the
 * control still renders; it just has nothing to borrow a label from. See
 * NO_REASONING_LABEL.
 */
export function EffortSelector({
  value,
  options,
  onChange,
  onLockedSelect,
  disabled = false,
  className,
}: EffortSelectorProps) {
  // Controlled so a selection closes the menu. Items live inside PopoverContent,
  // so Radix's outside-click never fires for them — and a locked pick opens the
  // paywall dialog, which sits below this popover in the stacking order.
  const [open, setOpen] = useState(false)

  const current = options.find((o) => o.value === value) ?? options[0]

  // No options at all: the model cannot reason, so there is no tier to name.
  // Deliberately NOT "Disabled" — that is the label for effort 'none', which is
  // a choice the user made. This is the absence of the choice, and conflating
  // the two would say the user turned something off that was never available.
  const label = current?.label ?? NO_REASONING_LABEL
  const noOptions = options.length === 0

  const handleSelect = (option: EffortOption) => {
    // Inert, not hidden: the popover still opens so the footer can explain WHY
    // there is nothing to choose. A control that vanishes as you switch models
    // teaches the user nothing.
    if (disabled) return
    setOpen(false)
    if (option.locked) onLockedSelect?.(option.value)
    else onChange(option.value)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          "inline-flex items-center gap-1.5 h-6 px-2 rounded-md text-caption text-muted-foreground",
          "hover:bg-hover hover:text-foreground cursor-pointer transition-colors",
          "motion-safe:active:scale-[0.97] motion-safe:transition-transform motion-safe:duration-100",
          className,
        )}
        aria-label={
          noOptions ? 'Reasoning: not supported by this model' : `Reasoning effort: ${label}`
        }
      >
        <Brain size={13} strokeWidth={1.75} className="shrink-0" />
        {label}
        <ChevronsUpDown size={11} strokeWidth={1.75} className="shrink-0 opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        {/* Names the axis. Without it "Low" and "Disabled" sitting next to a model
            name are ambiguous — low what? */}
        <p className="px-2 pt-1 pb-1.5 text-eyebrow uppercase tracking-wide text-muted-foreground">
          Reasoning
        </p>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => handleSelect(option)}
            // aria-disabled only for the no-reasoning model. A LOCKED row stays
            // enabled on purpose: it is actionable, it opens the plan picker.
            aria-disabled={disabled || undefined}
            className={cn(
              "w-full flex items-start gap-2 px-2 py-1.5 rounded-md text-left",
              disabled
                ? "opacity-50 cursor-not-allowed"
                : "cursor-pointer hover:bg-hover transition-colors",
            )}
          >
            <Check
              size={13}
              strokeWidth={2}
              className={cn(
                "shrink-0 mt-0.5",
                option.value === value ? "text-foreground" : "opacity-0",
              )}
            />
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 text-label text-foreground">
                {option.label}
                {option.locked && (
                  <>
                    <Lock
                      size={11}
                      strokeWidth={2}
                      className="shrink-0 text-warning"
                      aria-hidden="true"
                    />
                    <span className="sr-only">Upgrade required</span>
                  </>
                )}
              </span>
              <span className="text-caption text-muted-foreground">{option.description}</span>
            </span>
          </button>
        ))}
        {(disabled || noOptions) && (
          // The one thing the rows cannot say for themselves — and with no rows
          // at all it is the ONLY thing in the menu, which is why it is keyed on
          // an empty list too rather than on `disabled` alone. 128 of
          // OpenRouter's 414 models have no reasoning, so this is a normal
          // state, not an error.
          <p
            className={cn(
              "px-2 pb-1 text-caption text-muted-foreground",
              // No divider to sit under when there is nothing above it.
              noOptions ? "pt-1" : "pt-2 mt-1 border-t border-border-subtle",
            )}
          >
            This model does not support reasoning.
          </p>
        )}
      </PopoverContent>
    </Popover>
  )
}
